import SuccessScreen from 'components/UI/V3/SuccessScreen';
import React from 'react';

import { WizardStepper } from './WizardStepper';

import styles from './CampaignSubmittedScreen.module.scss';

interface CampaignSubmittedScreenProps {
    /** Campaign name surfaced in the success copy. */
    campaignName: string;
    /**
     * Drives the success message:
     *   - 'sent'      → "has been sent successfully."
     *   - 'scheduled' → "has been scheduled successfully."
     */
    mode: 'sent' | 'scheduled';
    /**
     * Click handler for both the top-right X and the "Go to Email Campaigns"
     * CTA — same exit semantics for both, mirroring the design.
     */
    onClose: () => void;
}

/**
 * Terminal screen of the campaign wizard — replaces the wizard layout after
 * a successful Send Now (real) or Schedule (mocked) confirmation.
 *
 * Body delegated to the V3 `SuccessScreen` primitive so the icon / title /
 * button styling matches Forms / Rooms / Tasks. The wizard chrome (logo +
 * stepper + close X) is preserved so the user sees the "all 3 steps complete"
 * victory state before exiting — mirrors the Tasks success pattern of keeping
 * the drawer header but disabling its back affordance.
 *
 * `WizardStepper` reuses its "all complete" state via `currentStep={4}` (one
 * past the last real step), which marks the 3 real steps as `completed`
 * without adding a new prop on the stepper.
 */
const CampaignSubmittedScreen: React.FC<CampaignSubmittedScreenProps> = ({ campaignName, mode, onClose }) => {
    const successCopy = mode === 'sent' ? 'has been sent successfully.' : 'has been scheduled successfully.';

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <img
                    src="/images/logo/logo.png"
                    alt="Cuddles"
                    className={styles.logo}
                />
                <WizardStepper
                    currentStep={4}
                    maxReachedStep={3}
                />
                <button
                    type="button"
                    className={styles.closeButton}
                    onClick={onClose}
                    aria-label="Close"
                >
                    ×
                </button>
            </div>

            <SuccessScreen
                title="Good Job"
                message={
                    <p className={styles.message}>
                        <strong>{campaignName}</strong> {successCopy}
                    </p>
                }
                buttonLabel="Go to Email Campaigns"
                buttonVariant="black"
                onClick={onClose}
            />
        </div>
    );
};

export default CampaignSubmittedScreen;
